import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// One page of sales is enough for a dashboard: it looks at the week around
// today, not the full history. 100 is the Marketplace API maximum.
const SALES_PAGE_SIZE = 100;

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const instructorDashboardPageSlice = createSlice({
  name: 'InstructorDashboardPage',
  initialState: {
    fetchInProgress: false,
    fetchDashboardError: null,
    transactionRefs: [],
    ownListings: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchDashboardError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const { salesResponse, listingsResponse } = action.payload;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(salesResponse.data.data);
        state.ownListings = listingsResponse.data.data;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.fetchInProgress = false;
        state.fetchDashboardError = action.payload;
      });
  },
});

export default instructorDashboardPageSlice.reducer;

// ================ Load data ================ //

// The same query the Inbox already runs for the provider side. Read-only:
// this page never transitions a transaction, it only displays them.
const loadDataPayloadCreator = (_args, { dispatch, rejectWithValue, extra: sdk }) => {
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const salesQueryParams = {
    only: 'sale',
    processNames,
    include: ['listing', 'customer', 'customer.profileImage', 'booking'],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      // Whatever the checkout collected: pickup address, lesson focus and any
      // other transaction fields configured in Console. Only the two parties to
      // the transaction can read this.
      'protectedData',
    ],
    'fields.listing': ['title', 'publicData.listingType', 'publicData.unitType'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.protectedData',
      'deleted',
      'banned',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page: 1,
    perPage: SALES_PAGE_SIZE,
  };

  const ownListingsQueryParams = {
    'fields.listing': ['title', 'state', 'price', 'publicData'],
    include: ['images'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    perPage: 25,
  };

  return Promise.all([
    sdk.transactions.query(salesQueryParams),
    sdk.ownListings.query(ownListingsQueryParams),
  ])
    .then(([salesResponse, listingsResponse]) => {
      dispatch(addMarketplaceEntities(salesResponse));
      return { salesResponse, listingsResponse };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const loadDataThunk = createAsyncThunk(
  'InstructorDashboardPage/loadData',
  loadDataPayloadCreator
);

export const loadData = (params, search) => dispatch => dispatch(loadDataThunk({ params, search }));
